import { useEffect, useState, type FormEvent } from "react";
import { Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { authClient, authErrorMessage, MIN_PASSWORD_LENGTH } from "@/lib/auth-client";

export function AccountDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: session, refetch } = authClient.useSession();
  const queryClient = useQueryClient();
  const user = session?.user;

  const [name, setName] = useState("");
  const [nameMsg, setNameMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [savingName, setSavingName] = useState(false);

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [revokeOthers, setRevokeOthers] = useState(true);
  const [pwMsg, setPwMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    if (open) {
      setName(user?.name ?? "");
      setNameMsg(null);
      setPwMsg(null);
      setCurrent("");
      setNext("");
      setConfirm("");
    }
  }, [open, user?.name]);

  async function saveName(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setNameMsg({ ok: false, text: "Name cannot be empty." });
      return;
    }
    setSavingName(true);
    setNameMsg(null);
    const { error } = await authClient.updateUser({ name: name.trim() });
    setSavingName(false);
    if (error) {
      setNameMsg({ ok: false, text: authErrorMessage(error) });
    } else {
      setNameMsg({ ok: true, text: "Name updated." });
      refetch();
      queryClient.invalidateQueries();
    }
  }

  async function savePassword(e: FormEvent) {
    e.preventDefault();
    if (next.length < MIN_PASSWORD_LENGTH) {
      setPwMsg({ ok: false, text: `New password must be at least ${MIN_PASSWORD_LENGTH} characters.` });
      return;
    }
    if (next !== confirm) {
      setPwMsg({ ok: false, text: "New passwords do not match." });
      return;
    }
    setSavingPw(true);
    setPwMsg(null);
    const { error } = await authClient.changePassword({
      currentPassword: current,
      newPassword: next,
      revokeOtherSessions: revokeOthers,
    });
    setSavingPw(false);
    if (error) {
      setPwMsg({ ok: false, text: authErrorMessage(error) });
    } else {
      setPwMsg({ ok: true, text: "Password changed." });
      setCurrent("");
      setNext("");
      setConfirm("");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Account</DialogTitle>
          <DialogDescription data-testid="text-account-email">
            Signed in as {user?.email}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={saveName} className="space-y-2">
          <Label htmlFor="account-name">Display name</Label>
          <div className="flex gap-2">
            <Input
              id="account-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              data-testid="input-account-name"
            />
            <Button type="submit" variant="secondary" disabled={savingName} data-testid="button-save-name">
              {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </div>
          {nameMsg && (
            <p className={`text-xs ${nameMsg.ok ? "text-muted-foreground" : "text-destructive"}`} role="status">
              {nameMsg.text}
            </p>
          )}
        </form>

        <div className="border-t border-border pt-4">
          <form onSubmit={savePassword} className="space-y-3">
            <div className="text-sm font-medium">Change password</div>
            <div className="space-y-1.5">
              <Label htmlFor="account-current">Current password</Label>
              <Input id="account-current" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required data-testid="input-current-password" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="account-new">New password</Label>
              <Input id="account-new" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={MIN_PASSWORD_LENGTH} data-testid="input-new-password" />
              <p className="text-xs text-muted-foreground">At least {MIN_PASSWORD_LENGTH} characters.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="account-confirm">Confirm new password</Label>
              <Input id="account-confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="input-confirm-new-password" />
            </div>
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <Checkbox checked={revokeOthers} onCheckedChange={(v) => setRevokeOthers(v === true)} data-testid="checkbox-revoke-sessions" />
              Sign out my other sessions
            </label>
            {pwMsg && (
              <p className={`text-sm ${pwMsg.ok ? "text-muted-foreground" : "text-destructive"}`} role="status" data-testid="text-password-status">
                {pwMsg.text}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={savingPw} data-testid="button-change-password">
              {savingPw && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Change password
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
